import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Upload, Trash2, Star, ArrowUp, ArrowDown, Pencil, Check, X, RefreshCw, Music } from "lucide-react";
import "./AdminBeats.css";

export default function AdminBeats({ backendApi, open, onClose, onChange }) {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [uploadBpm, setUploadBpm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editBpm, setEditBpm] = useState("");
  const fileInputRef = useRef(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem("ff_auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchBeats = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${backendApi}/beats`);
      setBeats(resp.data?.beats || []);
    } catch (e) {
      toast.error("Erreur de chargement des beats");
    } finally {
      setLoading(false);
    }
  }, [backendApi]);

  useEffect(() => {
    if (open) fetchBeats();
  }, [open, fetchBeats]);

  const notifyParent = () => { onChange && onChange(); };

  const onPickFile = () => fileInputRef.current?.click();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".mp3")) {
      toast.error("Fichier .mp3 uniquement");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (20 Mo max)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const params = new URLSearchParams();
      if (uploadName.trim()) params.set("name", uploadName.trim());
      if (uploadBpm.trim()) params.set("bpm", uploadBpm.trim());
      const url = `${backendApi}/admin/beats/upload${params.toString() ? `?${params}` : ""}`;
      await axios.post(url, form, {
        headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Beat ajouté");
      setUploadName(""); setUploadBpm("");
      await fetchBeats();
      notifyParent();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur upload";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const patchBeat = async (id, payload) => {
    try {
      await axios.patch(`${backendApi}/admin/beats/${id}`, payload, { headers: getAuthHeader() });
      await fetchBeats();
      notifyParent();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur de mise à jour";
      toast.error(msg);
    }
  };

  const startEdit = (beat) => {
    setEditingId(beat.id);
    setEditName(beat.name);
    setEditBpm(String(beat.bpm || ""));
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async () => {
    if (!editName.trim()) { toast.error("Nom requis"); return; }
    const bpm = parseInt(editBpm, 10);
    await patchBeat(editingId, {
      name: editName.trim(),
      bpm: isNaN(bpm) ? 0 : bpm,
    });
    setEditingId(null);
  };

  const toggleDefault = async (beat) => {
    if (beat.is_default) {
      toast.info("Au moins un beat doit rester par défaut");
      return;
    }
    await patchBeat(beat.id, { is_default: true });
    toast.success(`"${beat.name}" est maintenant le beat par défaut`);
  };

  const moveBeat = async (beat, direction) => {
    // direction: -1 = up, +1 = down. Swap order with neighbor.
    const sorted = [...beats].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(b => b.id === beat.id);
    const target = idx + direction;
    if (target < 0 || target >= sorted.length) return;
    const neighbor = sorted[target];
    // Swap orders
    await patchBeat(beat.id, { order: neighbor.order });
    await patchBeat(neighbor.id, { order: beat.order });
  };

  const deleteBeat = async (beat) => {
    if (!window.confirm(`Supprimer "${beat.name}" définitivement ?`)) return;
    try {
      await axios.delete(`${backendApi}/admin/beats/${beat.id}`, { headers: getAuthHeader() });
      toast.success("Beat supprimé");
      await fetchBeats();
      notifyParent();
    } catch (err) {
      toast.error("Erreur suppression");
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  if (!open) return null;

  return (
    <div className="admin-beats-overlay" onClick={onClose} data-testid="admin-beats-overlay">
      <div className="admin-beats-modal" onClick={(e) => e.stopPropagation()} data-testid="admin-beats-modal">
        <div className="admin-beats-header">
          <div className="admin-beats-title">
            <Shield size={18} />
            <h2>Admin — Bibliothèque de beats</h2>
          </div>
          <button className="admin-beats-close" onClick={onClose} data-testid="admin-beats-close-btn" title="Fermer">
            <X size={18} />
          </button>
        </div>

        <div className="admin-beats-section">
          <div className="admin-beats-section-title">Ajouter un beat</div>
          <div className="admin-beats-upload-row">
            <input
              type="text"
              className="admin-beats-input"
              placeholder="Nom (optionnel)"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              data-testid="admin-beats-upload-name"
            />
            <input
              type="number"
              className="admin-beats-input admin-beats-input-bpm"
              placeholder="BPM"
              value={uploadBpm}
              onChange={(e) => setUploadBpm(e.target.value)}
              data-testid="admin-beats-upload-bpm"
            />
            <button
              className="admin-beats-btn admin-beats-btn-primary"
              onClick={onPickFile}
              disabled={uploading}
              data-testid="admin-beats-upload-btn"
            >
              <Upload size={14} />
              {uploading ? "Upload..." : "Choisir un MP3"}
            </button>
          </div>
          <p className="admin-beats-hint">Format MP3, 20 Mo max. Le nouveau beat sera ajouté en fin de liste.</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mpeg,audio/mp3,.mp3"
            style={{ display: "none" }}
            onChange={handleUpload}
          />
        </div>

        <div className="admin-beats-section">
          <div className="admin-beats-section-header">
            <div className="admin-beats-section-title">Beats disponibles ({beats.length})</div>
            <button className="admin-beats-btn-ghost" onClick={fetchBeats} title="Rafraîchir" data-testid="admin-beats-refresh-btn">
              <RefreshCw size={14} className={loading ? "ab-spin" : ""} />
            </button>
          </div>

          {beats.length === 0 ? (
            <div className="admin-beats-empty">Aucun beat. Ajoute-en un ci-dessus.</div>
          ) : (
            <div className="admin-beats-list">
              {[...beats].sort((a, b) => a.order - b.order).map((beat, idx, arr) => (
                <div key={beat.id} className={`admin-beats-item ${beat.is_default ? "is-default" : ""}`} data-testid={`admin-beat-${beat.id}`}>
                  <button
                    className={`admin-beats-icon-btn admin-beats-star ${beat.is_default ? "is-default" : ""}`}
                    onClick={() => toggleDefault(beat)}
                    title={beat.is_default ? "Beat par défaut actuel" : "Définir comme beat par défaut"}
                    data-testid={`admin-beat-star-${beat.id}`}
                  >
                    <Star size={14} fill={beat.is_default ? "currentColor" : "none"} />
                  </button>

                  {editingId === beat.id ? (
                    <>
                      <input
                        className="admin-beats-input admin-beats-edit-name"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                        autoFocus
                      />
                      <input
                        type="number"
                        className="admin-beats-input admin-beats-input-bpm"
                        value={editBpm}
                        onChange={(e) => setEditBpm(e.target.value)}
                        placeholder="BPM"
                      />
                      <button className="admin-beats-icon-btn" onClick={saveEdit} title="Enregistrer" data-testid={`admin-beat-save-${beat.id}`}>
                        <Check size={14} />
                      </button>
                      <button className="admin-beats-icon-btn" onClick={cancelEdit} title="Annuler">
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="admin-beats-info">
                        <div className="admin-beats-name">
                          <Music size={12} className="ab-music-icon" />
                          {beat.name}
                        </div>
                        <div className="admin-beats-meta">
                          <span>{beat.bpm} BPM</span>
                          <span>·</span>
                          <span>{beat.has_storage ? "Uploadé" : "Statique"}</span>
                          <span>·</span>
                          <span>{formatSize(beat.size)}</span>
                        </div>
                      </div>
                      <button
                        className="admin-beats-icon-btn"
                        onClick={() => moveBeat(beat, -1)}
                        disabled={idx === 0}
                        title="Monter"
                        data-testid={`admin-beat-up-${beat.id}`}
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        className="admin-beats-icon-btn"
                        onClick={() => moveBeat(beat, +1)}
                        disabled={idx === arr.length - 1}
                        title="Descendre"
                        data-testid={`admin-beat-down-${beat.id}`}
                      >
                        <ArrowDown size={14} />
                      </button>
                      <button
                        className="admin-beats-icon-btn"
                        onClick={() => startEdit(beat)}
                        title="Renommer / changer BPM"
                        data-testid={`admin-beat-edit-${beat.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="admin-beats-icon-btn admin-beats-danger"
                        onClick={() => deleteBeat(beat)}
                        title="Supprimer"
                        data-testid={`admin-beat-delete-${beat.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
